<script>
import moment from 'moment';
import store from '@store';
import employeeService from '@services/employeeService';
import AkAvatar from '@components/general/AkAvatar.vue';
import UtilsMixin from '@mixins/utilsMixin';

const displayCache = {};

export default {
  name: 'InternalNote',
  components: {AkAvatar},
  mixins: [UtilsMixin],
  props: {
    note: {
      type: Object,
      required: true,
    },
    lastInternalNotesRead: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['delete'],
  data() {
    return {
      showPopover: false,
      popoverStyle: {},
      readers: [],
    };
  },
  computed: {
    isOwn() {
      return this.note.author.id === store.state.userInformation.userId;
    },
    authorName() {
      const parts = this.note.author.display.split(' ');
      return {
        firstName: parts[0],
        lastName: parts[1],
      };
    },
    validMarkers() {
      return this.lastInternalNotesRead.filter(
        (r) => r.employeeId !== this.note.employeeId && new Date(r.dateRead) >= new Date(this.note.dateCreated),
      );
    },
    readerMarkers() {
      const currentEmployeeId = store.state.userInformation.employeeId;
      return this.validMarkers.filter((r) => r.employeeId !== currentEmployeeId);
    },
    readByMeOnly() {
      const currentEmployeeId = store.state.userInformation.employeeId;
      return this.validMarkers.length === 1 && this.validMarkers[0].employeeId === currentEmployeeId;
    },
    showReadIndicator() {
      return !this.readByMeOnly;
    },
    isRead() {
      return this.readerMarkers.length > 0;
    },
  },
  methods: {
    formatDateRead(dateRead) {
      return moment.utc(dateRead).local().format('DD MMM [à] HH[h]mm');
    },
    updatePopoverPosition() {
      const rect = this.$refs.trigger.getBoundingClientRect();
      const popoverWidth = 220;
      const left = Math.min(Math.max(rect.right - popoverWidth, 8), window.innerWidth - popoverWidth - 8);
      this.popoverStyle = {
        left: `${left}px`,
        bottom: `${window.innerHeight - rect.top + 8}px`,
      };
    },
    onMouseEnter() {
      if (!this.isRead) return;
      this.updatePopoverPosition();
      this.showPopover = true;
      this.loadReaders();
    },
    loadReaders() {
      this.readers = this.readerMarkers.map((r) => ({
        employeeId: r.employeeId,
        dateRead: r.dateRead,
        display: displayCache[r.employeeId]?.display,
      }));
      this.readers
        .filter((r) => !r.display)
        .forEach((r) => {
          employeeService.employeeDisplay(r.employeeId).then((display) => {
            displayCache[r.employeeId] = display;
            r.display = display.display;
          });
        });
    },
  },
};
</script>

<template>
  <div class="flex flex-col gap-1">
    <div
      class="flex items-start justify-between gap-2 p-4 rounded-md border border-dashed border-grey-300 bg-[#F7F9FB]">
      <span class="text-grey-900 whitespace-pre-wrap break-words">{{ note.content }}</span>
      <button v-if="isOwn" class="shrink-0" @click="$emit('delete', note)">
        <span class="ga-icon ga-trash text-lg text-red-500" />
      </button>
    </div>
    <div class="flex items-center gap-2 px-1">
      <AkAvatar
        :first-name="authorName.firstName"
        :last-name="authorName.lastName"
        :user-id="note.author.id"
        size="medium"
        class="shrink-0 [&_.profile>div]:!h-6 [&_.profile>div]:!w-6" />
      <span class="text-sm font-medium text-grey-500">
        Note de {{ note.author.display }} - {{ displayDateTime(note.dateCreated) }}
      </span>
      <div
        v-if="showReadIndicator"
        ref="trigger"
        class="flex items-center ml-auto"
        :class="isRead ? 'text-secondary' : 'text-grey-300'"
        @mouseenter="onMouseEnter"
        @mouseleave="showPopover = false">
        <i class="ga-icon ga-check text-md" />
        <i class="ga-icon ga-check text-md -ml-2.5" />
      </div>
    </div>
    <Teleport to="body">
      <div
        v-if="showPopover"
        class="fixed bg-white rounded-xl shadow-[0_4px_16px_rgba(0,0,0,0.08)] border border-grey-100 min-w-[220px] z-[10001] p-3 flex flex-col gap-1"
        :style="popoverStyle">
        <span class="text-[13px] font-medium text-grey-600 mb-1">Lu par</span>
        <div v-for="reader in readers" :key="reader.employeeId" class="flex items-center justify-between gap-3 text-sm">
          <span class="font-medium text-sm text-grey-900 truncate">{{ reader.display || '…' }}</span>
          <span class="text-sm font-medium whitespace-nowrap text-grey-500">
            {{ formatDateRead(reader.dateRead) }}
          </span>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<style scoped></style>
